import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import { colors } from './theme';
import { formatDDMMYYYY } from './utils/dates';
import LoadingSpinner from './components/LoadingSpinner';

const API_BASE = process.env.REACT_APP_API_URL;

const TABS = [
  { key: 'all',             label: 'All' },
  { key: 'repairing',       label: 'In progress' },
  { key: 'recommissioned',  label: 'Recommissioned' },
  { key: 'scrapped',        label: 'Scrapped' },
];

function Badge({ status }) {
  const map = {
    pending:        { label: 'Pending',         bg: 'rgba(255,255,255,0.07)', color: '#aaa' },
    repairing:      { label: 'In progress',     bg: 'rgba(251,146,60,0.12)',  color: '#fb923c' },
    recommissioned: { label: 'Recommissioned',  bg: 'rgba(70,187,137,0.12)', color: '#46bb89' },
    scrapped:       { label: 'Scrapped',        bg: 'rgba(248,113,113,0.12)', color: '#f87171' },
  };
  const s = map[status] || map.pending;
  return (
    <span style={{
      padding: '0.18rem 0.6rem', borderRadius: '999px', fontSize: '0.7rem',
      fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
      backgroundColor: s.bg, color: s.color, whiteSpace: 'nowrap'
    }}>
      {s.label}
    </span>
  );
}

export default function RefurbishedPage() {
  const { getToken } = useAuth();
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const token = await getToken();
        const res = await fetch(`${API_BASE}/rma/refurbished-list`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (data.ok) setUnits(data.units || []);
        else setError('Failed to load');
      } catch {
        setError('Network error');
      } finally {
        setLoading(false);
      }
    })();
  }, [getToken]);

  const filtered = tab === 'all'
    ? units
    : units.filter(u => u.refurbishment?.status === tab);

  const totalCost = units
    .filter(u => u.refurbishment?.status === 'recommissioned' && u.refurbishment?.repairCost != null)
    .reduce((sum, u) => sum + (u.refurbishment.repairCost || 0), 0);

  const recommissionedCount = units.filter(u => u.refurbishment?.status === 'recommissioned').length;
  const scrappedCount = units.filter(u => u.refurbishment?.status === 'scrapped').length;
  const inProgressCount = units.filter(u => u.refurbishment?.status === 'repairing').length;

  return (
    <div style={{ minHeight: '100vh', background: colors.bg || '#0b0f0d', color: colors.textBody }}>
      {/* Header */}
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        borderBottom: '1px solid rgba(255,255,255,0.07)',
        padding: '1rem 1.5rem',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <Link to="/org/devices" style={{ color: colors.textMuted, textDecoration: 'none', fontSize: '0.85rem' }}>
            ← Service
          </Link>
          <div style={{ width: '1px', height: '16px', background: 'rgba(255,255,255,0.12)' }} />
          <h1 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: colors.textStrong }}>
            Refurbished Units
          </h1>
        </div>
      </div>

      <div style={{ padding: '1.5rem', maxWidth: '960px', margin: '0 auto' }}>

        {/* Stats row */}
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          {[
            { label: 'Total units', value: units.length, color: colors.textStrong },
            { label: 'In progress',  value: inProgressCount, color: '#fb923c' },
            { label: 'Recommissioned', value: recommissionedCount, color: '#46bb89' },
            { label: 'Scrapped',     value: scrappedCount,   color: '#f87171' },
            ...(totalCost > 0 ? [{ label: 'Total repair cost', value: `${totalCost.toFixed(2)} EUR`, color: colors.textStrong }] : []),
          ].map(({ label, value, color }) => (
            <div key={label} style={{
              borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)',
              backgroundColor: 'rgba(255,255,255,0.03)', padding: '0.75rem 1.1rem',
              display: 'flex', flexDirection: 'column', gap: '0.2rem', minWidth: '110px'
            }}>
              <span style={{ fontSize: '0.68rem', color: colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
              <span style={{ fontSize: '1.2rem', fontWeight: 700, color }}>{value}</span>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '0.25rem', marginBottom: '1.25rem', borderBottom: '1px solid rgba(255,255,255,0.07)', paddingBottom: '0' }}>
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{
                padding: '0.5rem 1rem', background: 'none', border: 'none',
                borderBottom: tab === t.key ? `2px solid ${colors.accent}` : '2px solid transparent',
                color: tab === t.key ? colors.accent : colors.textMuted,
                fontWeight: tab === t.key ? 700 : 500, fontSize: '0.88rem',
                cursor: 'pointer', transition: 'color 0.12s', marginBottom: '-1px'
              }}
            >
              {t.label}
              <span style={{
                marginLeft: '0.4rem', padding: '0.1rem 0.45rem', borderRadius: '999px',
                fontSize: '0.7rem', fontWeight: 700,
                backgroundColor: tab === t.key ? 'rgba(70,187,137,0.15)' : 'rgba(255,255,255,0.06)',
                color: tab === t.key ? colors.accent : colors.textMuted
              }}>
                {t.key === 'all' ? units.length : units.filter(u => u.refurbishment?.status === t.key).length}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        {loading ? (
          <div style={{ padding: '3rem', display: 'flex', justifyContent: 'center' }}>
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div style={{ color: '#f87171', padding: '2rem', textAlign: 'center' }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={{ color: colors.textMuted, padding: '3rem', textAlign: 'center', fontSize: '0.9rem' }}>
            No units in this category yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {/* Column headers */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1.4fr 1.2fr 1.2fr 1fr 1fr 0.9fr',
              gap: '0.75rem', padding: '0.4rem 0.85rem',
              fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase',
              letterSpacing: '0.07em', color: colors.textMuted
            }}>
              <span>Refurb ID</span>
              <span>Serial</span>
              <span>Original RMA</span>
              <span>Status</span>
              <span>Cost</span>
              <span>Date</span>
            </div>

            {filtered.map(unit => {
              const r = unit.refurbishment || {};
              const dateVal = r.status === 'recommissioned'
                ? r.recommissionedAt
                : r.status === 'scrapped'
                  ? r.decidedAt
                  : r.repairStartedAt;
              return (
                <div
                  key={unit._id}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1.4fr 1.2fr 1.2fr 1fr 1fr 0.9fr',
                    gap: '0.75rem', padding: '0.75rem 0.85rem',
                    borderRadius: '10px', border: '1px solid rgba(255,255,255,0.07)',
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    alignItems: 'center', fontSize: '0.86rem'
                  }}
                >
                  <span style={{ fontFamily: 'monospace', fontWeight: 700, color: colors.accent, fontSize: '0.82rem' }}>
                    {r.refurbId || '—'}
                  </span>
                  <span style={{ color: colors.textStrong, fontWeight: 600 }}>{unit.serialNumber || '—'}</span>
                  <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>{unit.rmaId || unit._id?.slice(-8)}</span>
                  <Badge status={r.status} />
                  <span style={{ color: colors.textBody }}>
                    {r.repairCost != null ? `${r.repairCost} ${r.repairCurrency || 'EUR'}` : '—'}
                  </span>
                  <span style={{ color: colors.textMuted, fontSize: '0.8rem' }}>
                    {dateVal ? formatDDMMYYYY(dateVal) : '—'}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
